import type { Note } from '../song';
import { midi, transpose } from '../score';
import { DREAM_PLAN, KEYS, flavored } from './dream';
import type { Bar, Bass, Chord, Ctx } from './dream';

const LYDIAN = [0, 2, 4, 6, 7, 9, 11] as const;

const CHORDS: Readonly<Record<string, Chord>> = {
  'Cmaj7#11': { root: 'C2', pad: ['E3', 'B3', 'D4', 'F#4'] },
  Dadd9: { root: 'D2', pad: ['F#3', 'A3', 'D4', 'E4'] },
  Bm7: { root: 'B1', pad: ['D3', 'A3', 'B3', 'F#4'] },
  Em9: { root: 'E2', pad: ['G3', 'B3', 'D4', 'F#4'] },
  Am9: { root: 'A1', pad: ['G3', 'B3', 'C4', 'E4'] },
  Gmaj7: { root: 'G1', pad: ['F#3', 'B3', 'D4', 'A4'] },
};

const HOOK: Note[][] = [
  [[0, 'B5', 3], [3, 'G5', 1], [4, 'F#5', 2], [6, 'E5', 2], [8, 'G5', 3], [11, 'B5', 1], [12, 'D6', 4]],
  [[0, 'A5', 3], [3, 'F#5', 1], [4, 'E5', 2], [6, 'D5', 2], [8, 'F#5', 3], [11, 'A5', 1], [12, 'E6', 4]],
  [[0, 'F#5', 3], [3, 'D5', 1], [4, 'B4', 2], [6, 'D5', 2], [8, 'F#5', 2], [10, 'A5', 2], [12, 'B5', 4]],
  [[0, 'G5', 2], [2, 'B5', 2], [4, 'F#5', 4], [8, 'E5', 2], [10, 'D5', 2], [12, 'E5', 4]],
];

const BOUNCE: Bass = [0, 4, 8, 12].flatMap((st) => [
  [st, 0, 1.8, st % 8 === 0 ? 0.9 : 0.8],
  [st + 2, 12, 1.2, 0.65],
] as const);

function extra(x: Ctx, b: Bar): void {
  const top = b.bells.slice(4);
  const jit = () => 0.9 + x.rng.float() * 0.2;
  if (b.end) return;
  if (b.drop) for (const st of [3, 6, 11, 14]) x.play('pluck', b.bar, st, x.rng.pick(top), x.s * 1.5, 0.55 * jit());
  else if (b.groove && x.e >= 0.6) for (const st of [6, 14]) x.play('pluck', b.bar, st, x.rng.pick(top), x.s * 1.5, 0.5 * jit());
  const shim = (b.drop && b.index % 2 === 0) || b.name === 'break' || (b.name === 'intro' && b.index === 0);
  if (shim) x.chord('shim', b.bar, 0, top, x.g.barLength * (b.name === 'break' ? 1 : 2), 0.45, 0.04);
}

export const CRISTAIS = flavored({
  id: 'cristais',
  genre: 'crystal electronica / glass-bell pop',
  bpm: [120, 126],
  ref: { seed: 'geodo', title: 'Geodo', params: { bpm: 122, bars: 28, energy: 0.55 } },
  chords: CHORDS,
  progs: [
    ['Cmaj7#11', 'Dadd9', 'Bm7', 'Em9'],
    ['Cmaj7#11', 'Gmaj7', 'Dadd9', 'Em9'],
    ['Em9', 'Cmaj7#11', 'Dadd9', 'Bm7'],
    ['Am9', 'Cmaj7#11', 'Gmaj7', 'Dadd9'],
  ],
  hook: HOOK,
  arps: [
    [0, 4, 1, 5, 2, 6, 3, 7, 4, 0, 5, 1, 6, 2, 7, 3],
    [0, 1, 2, 3, 4, 5, 6, 7, 6, 5, 4, 3, 2, 3, 4, 5],
    [7, 5, 6, 4, 5, 3, 4, 2, 3, 1, 2, 0, 1, 3, 5, 7],
    [0, 2, 4, 6, 7, 5, 3, 1, 0, 2, 4, 6, 7, 6, 4, 2],
  ],
  keys: KEYS,
  scale: LYDIAN,
  range: [74, 90],
  center: 81,
  plan: DREAM_PLAN,
  swing: 52,
  lufs: -10.5,
  mix: (bpm) => ({ reverbDecay: 4.8, delayTime: (60 / bpm) * 0.75, delayFeedback: 0.38, drive: 1.15, softness: 1.5, pumpDepth: 0.3, pumpRelease: 0.3 }),
  kick: { groove: 'X.......X.......', drop: 'X...X...X...X...' },
  back: { groove: '....x.......x...', drop: '....x.......x...', vel: [0.7, 0.9] },
  hats: ['..x...x...x...x.', 'o-x-o-x-o-x-o-x-', 'oox-oox-oox-ooxo'],
  open: { pattern: '..x...x...x...x.', from: 0.6 },
  bass: {
    groove: [
      [0, 0, 7.5, 0.9],
      [8, 0, 3.5, 0.8],
      [12, 7, 3.5, 0.7],
    ],
    drop: BOUNCE,
  },
  arp: { groove: 'X.x.X.xxX.x.X.xx', drop: 'XxxxXxxxXxxxXxxx', len: 3 },
  bells: (pad) => [...pad.map((n) => transpose(n, 12)), ...pad.map((n) => transpose(n, 24))].sort((a, b) => midi(a) - midi(b)),
  extra,
  accents: { kick: 'kick', snare: 'snare', pad: 'stab', lead: 'melody', impact: 'drop' },
});
